import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    applyConfig,
    fetchThemeFromApi,
    getCachedConfig,
    saveThemeToApi,
    type ThemeConfig,
} from '@/lib/theme';
import { resolveBrand } from '@/brand';

interface ThemeContextValue {
    /** Config atualmente aplicada (apos sync com backend). */
    config: ThemeConfig;
    /** Flag de carregamento da chamada inicial ao backend. */
    isLoading: boolean;
    /**
     * Salva nova config no backend e atualiza estado local.
     * Aplica imediato em :root via applyConfig - sidebar e demais
     * componentes consumindo `useTheme` re-renderizam.
     */
    saveConfig: (next: ThemeConfig) => Promise<void>;
    /** Forca refetch do backend. */
    refetch: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
    children: React.ReactNode;
    /** Se true, busca theme do backend ao montar. False em rotas publicas (login). */
    fetchOnMount?: boolean;
}

export function ThemeProvider({ children, fetchOnMount = true }: ThemeProviderProps) {
    // Estado inicial: cache local (ja aplicado no main.tsx ANTES da arvore montar)
    const [config, setConfig] = useState<ThemeConfig>(() => getCachedConfig());
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const fetchedRef = useRef<boolean>(false);

    const refetch = useCallback(async () => {
        // Brands white-label (VITE_BRAND != 'just') nao dependem do backend
        // pra tema: a paleta ja foi aplicada em main.tsx a partir do brand
        // package. Evita que uma customizacao via /admin/design-system feita
        // numa instancia JUST compartilhando o mesmo backend vaze pro brand
        // white-label (a config de tema e global, nao por tenant).
        if (resolveBrand().id !== 'just') return;
        setIsLoading(true);
        try {
            const fresh = await fetchThemeFromApi();
            applyConfig(fresh);
            setConfig(fresh);
        } catch {
            // Falha no backend: mantem o cache aplicado. Nao bloqueia a app.
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!fetchOnMount) return;
        if (fetchedRef.current) return;
        fetchedRef.current = true;
        void refetch();
    }, [fetchOnMount, refetch]);

    const saveConfig = useCallback(async (next: ThemeConfig) => {
        const persisted = await saveThemeToApi(next);
        applyConfig(persisted);
        setConfig(persisted);
    }, []);

    const value = useMemo<ThemeContextValue>(
        () => ({ config, isLoading, saveConfig, refetch }),
        [config, isLoading, saveConfig, refetch],
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
    const ctx = useContext(ThemeContext);
    if (!ctx) {
        throw new Error('useTheme deve ser usado dentro de <ThemeProvider>');
    }
    return ctx;
}
